<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import type { CashDrawer } from '../../../../types';
import { toCents } from '@Helpers';
import { useSessionStore } from '@Stores';
import { useAToast, useADialog } from '@Atoms';
import { ROLES } from '@AuthModule';
import { useForm } from '@FormModule';
import getSanitizedPayload from './getSanitizedPayload.helper';
import useCartStore from '../../../../store';
import useFields from './useFields';
import { CART_STEPS, CUSTOM_OUTCOME_TYPE, PAYMENT_METHODS } from '../../../../types';
import { CART_TYPES } from '../../../../enums';
import {
  CompleteOutcomeService,
  CompleteSplitPaymentService,
  PostCitationPaymentService,
  ProcessStripePaymentService,
  UpdatePaymentTypeService,
} from '../../../../services';
import { useCartSteps } from '../../../../composables';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { can } = useSessionStore();
const {
  animals,
  cartData,
  completeOutcomeRoute,
  confirmingCartWithTotalZero,
  hasCart,
  isAdoption,
  isCitation,
  isDonation,
  isProduct,
  isReturnToOwner,
  loading,
  outcomeType,
} = storeToRefs(useCartStore());
const { outcomeCompletedCartReset } = useCartStore();
const { shelterProfile } = storeToRefs(useSessionStore());

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { currentStep, previousStep } = useCartSteps();
const { currentSchemaModels, fieldsSchema, formError, paymentMethodModel, resetFieldSchema, setForm } = useFields();
const { fieldsValidation } = useForm();
const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const requestErrorMessage = ref('');
const sendDocupet = ref(true);
const sendHills = ref(true);

// DocuPet: Adoption, RTO, Clinic Out, Service Out only. Hills: Adoption only. Never for intakes (isOutcome false).
const OUTCOME_TYPES_FOR_DOCUPET = ['adopted', 'return_to_owner', 'clinic_out', 'service_out'];
const showDocupet = computed(
  () =>
    isOutcome.value && !!shelterProfile.value?.docupet_enabled && OUTCOME_TYPES_FOR_DOCUPET.includes(outcomeType.value)
);
const showHills = computed(
  () => isOutcome.value && !!shelterProfile.value?.hills_report && outcomeType.value === 'adopted'
);
const showThirdPartyIntegrations = computed(() => showDocupet.value || showHills.value);

const isRetroactivePayment = computed(() => cartData.value?.paid_date);
const isOutcome = computed(() => cartData.value?.cart_type === CART_TYPES.OUTCOME);
const preAdoptFlags = computed(() => ({
  enablePrePaymentForRtos: isReturnToOwner.value && shelterProfile.value?.enable_pre_payment_for_rtos,
  enablePreAdoptForAdoptions: isAdoption.value && shelterProfile.value?.enable_pre_adopt_for_adoptions,
}));
const showPrePaymentOption = computed(
  () =>
    isOutcome.value && (preAdoptFlags.value.enablePreAdoptForAdoptions || preAdoptFlags.value.enablePrePaymentForRtos)
);
const shelterHasOffsiteAdoption = computed(
  () =>
    shelterProfile.value?.has_offsite_adoption &&
    can(ROLES.OFFSITE_ANIMAL) &&
    isAdoption.value &&
    !isRetroactivePayment.value
);
const disableOffsiteButton = computed(
  () =>
    loading.value ||
    [PAYMENT_METHODS.CREDIT, PAYMENT_METHODS.SPLIT_PAYMENT].includes(paymentMethodModel.value as PAYMENT_METHODS)
);
const cartItemType = computed(() =>
  //prettier-ignore
  isCitation.value ? 'citation' :
  isProduct.value ? 'product' :
  isDonation.value ? 'donation' :
  undefined
);

const preAdoptLabel = computed(() =>
  // prettier-ignore
  !isOutcome.value ? ''
  :isAdoption.value ? 'cart.label.preAdopt'
  : isReturnToOwner.value ? 'cart.label.prePayment'
  : ''
);

watch(confirmingCartWithTotalZero, () => onConfirmCardWithTotalZero());
watch(paymentMethodModel, () => updatePaymentType());

onMounted(() => onStepInit());

// HELPERS
function setRequestErrorMessage(error: any) {
  requestErrorMessage.value = error?.response?.data?.error || 'cart.toast.defaultError.error.message';
}

function getCashDrawer(): CashDrawer | undefined {
  return shelterProfile.value?.cash_drawer
    ? fieldsSchema.value.find(({ name }) => name === 'cash_drawer')?.model
    : undefined;
}

// SERVICES
function updatePaymentType() {
  if (!paymentMethodModel.value) return;

  requestErrorMessage.value = '';

  const PAYLOAD = {
    payment_type: paymentMethodModel.value,
  };

  UpdatePaymentTypeService(PAYLOAD)
    .then(({ data }) => (cartData.value = data))
    .catch(() => notify(TOAST_ERROR));
}

// EVENTS
function onStepInit() {
  currentStep.value = CART_STEPS.PAYMENT_METHOD;
  resetFieldSchema();
  setForm();
}

function onNextStep(nextStep: 'offsite' | 'preAdopt' | null) {
  if (loading.value) return;

  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  if (hasErrors) return (formError.value = true);

  const animalsBaseRtoSanitezed = animals.value.map((animal: any) => {
    const needsAdjustments = animal.base_rto_fee && typeof animal.base_rto_fee === 'object';

    if (!needsAdjustments) return animal;

    return {
      ...animal,
      base_rto_fee: animal.base_rto_fee.amount.toString() || 0,
      outcome: {
        ...animal.outcome,
        fee: animal.base_rto_fee.amount.toString(),
      },
    };
  });

  const isSplitPayment = paymentMethodModel.value === PAYMENT_METHODS.SPLIT_PAYMENT;
  const isCreditCard = paymentMethodModel.value === PAYMENT_METHODS.CREDIT;
  const isOffsite = nextStep === 'offsite';
  const isPreAdopt = nextStep === 'preAdopt';

  let payload = {
    ...cartData.value,
    ...getSanitizedPayload(currentSchemaModels.value, shelterProfile.value, isSplitPayment),
    payment_type: paymentMethodModel.value, // Use UI model directly to avoid race condition with async updatePaymentType()
    split_payment: isSplitPayment,
    animals: animalsBaseRtoSanitezed,
    send_docupet: showDocupet.value ? sendDocupet.value : undefined,
    send_hills: showHills.value ? sendHills.value : undefined,
  };

  const isCash = paymentMethodModel.value === PAYMENT_METHODS.CASH;
  const hasAmountCollected = payload.cash_amount_collected != null;
  if (isCash && hasAmountCollected) {
    const totalCharge = cartData.value?.total_charge ?? cartData.value?.total ?? 0;
    const changeCents = toCents(payload.cash_amount_collected) - toCents(totalCharge);

    payload.cash_change_due = changeCents / 100;
  }

  if (isOffsite) return onOffSitePending(payload);
  if (isPreAdopt) return onPreAdopt(payload);

  const cartWithTotalZero = !Number(cartData.value?.total) && hasCart.value;
  if (cartWithTotalZero) {
    activeModal.value = SYSTEM_MODALS.CART_ZERO_TOTAL_CONFIRMATION;
    activeModalProps.value = {
      cartPayload: payload,
    };
    dialogVisibility.value = true;
    loading.value = false;
    return;
  }

  loading.value = true;

  // prettier-ignore
  const TargetService = isSplitPayment ? CompleteSplitPaymentService
    : isCitation.value ? PostCitationPaymentService
    : isCreditCard ? ProcessStripePaymentService
    : CompleteOutcomeService;

  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  TargetService(payload)
    .then(
      () => !!completeOutcomeRoute.value && push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset())
    )
    .catch(async (error) => {
      // NOTE :: Duplicate completion error handling
      if (error?.response?.status === 409 && completeOutcomeRoute.value) {
        await push(completeOutcomeRoute.value);
        outcomeCompletedCartReset();
        return;
      }

      notify(TOAST_ERROR);
      setRequestErrorMessage(error);
    })
    .finally(() => {
      loading.value = false;
      dialogVisibility.value = false;
    });
}

function onOffSitePending(payload: any) {
  activeModal.value = SYSTEM_MODALS.CART_PRE_PAYMENT;

  const { cart_id, paid_date } = cartData.value;

  activeModalProps.value = {
    cardReaderLastFour: payload.last_four_digits,
    checkNumber: payload?.check_number,
    cartId: cart_id.$oid,
    cashDrawer: getCashDrawer(),
    customOutcomeType: CUSTOM_OUTCOME_TYPE.OFFSITE,
    isCompleteOutcome: false,
    isOffsite: true,
    paidDate: paid_date,
    paymentType: payload.payment_type,
    ...payload,
  };

  dialogVisibility.value = true;
}

function onPreAdopt(payload: any) {
  activeModal.value = SYSTEM_MODALS.CART_PRE_PAYMENT;

  const { cart_id, paid_date } = cartData.value;

  activeModalProps.value = {
    cardReaderLastFour: payload?.last_four_digits,
    checkNumber: payload?.check_number,
    cartId: cart_id.$oid || cart_id,
    cashDrawer: getCashDrawer(),
    customOutcomeType: CUSTOM_OUTCOME_TYPE.PRE_ADOPT,
    isCompleteOutcome: false,
    isOffsite: false,
    isRto: isReturnToOwner,
    paidDate: paid_date,
    paymentType: payload.payment_type,
    ...payload,
  };

  dialogVisibility.value = true;
}

function onConfirmCardWithTotalZero() {
  if (loading.value) return;

  const flowIssue = !confirmingCartWithTotalZero.value || !activeModalProps.value?.cartPayload;
  if (flowIssue) {
    requestErrorMessage.value = 'Something went wrong while confirming you submission. Please try again.';
    return;
  }

  const payload = activeModalProps.value.cartPayload;
  const isSplitPayment = paymentMethodModel.value === PAYMENT_METHODS.SPLIT_PAYMENT;
  const isCreditCard = paymentMethodModel.value === PAYMENT_METHODS.CREDIT;

  loading.value = true;

  // prettier-ignore
  const TargetService = isSplitPayment ? CompleteSplitPaymentService
    : isCitation.value ? PostCitationPaymentService
    : isCreditCard ? ProcessStripePaymentService
    : CompleteOutcomeService;

  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  TargetService(payload)
    .then(
      () => !!completeOutcomeRoute.value && push(completeOutcomeRoute.value).then(() => outcomeCompletedCartReset())
    )
    .catch(async (error) => {
      if (error?.response?.status === 409 && completeOutcomeRoute.value) {
        await push(completeOutcomeRoute.value);
        outcomeCompletedCartReset();
        return;
      }

      notify(TOAST_ERROR);
      setRequestErrorMessage(error);
    })
    .finally(() => {
      loading.value = false;
      dialogVisibility.value = false;
    });
}
</script>

<template>
  <div class="flex h-fit flex-col gap-6">
    <main class="flex-1">
      <FormTemplate
        class="max-w-[500px]"
        :formError
        :schema="fieldsSchema"
      />

      <div class="flex flex-col gap-0 pt-4">
        <AFormError :formError />

        <Message
          v-if="isDonation && paymentMethodModel === PAYMENT_METHODS.CREDIT"
          pt:text="text-sm"
          severity="info"
          :closable="false"
        >
          {{ $t('cart.label.updatePaidDateForDonation') }}
        </Message>

        <Message
          v-if="requestErrorMessage"
          pt:text="text-sm"
          severity="error"
          @close="requestErrorMessage = ''"
        >
          {{ $t(requestErrorMessage) }}
        </Message>
      </div>

      <div v-if="paymentMethodModel && paymentMethodModel !== PAYMENT_METHODS.SPLIT_PAYMENT">
        <Divider
          class="before:border-t-2"
          type="dashed"
        />

        <div class="flex max-w-[500px] flex-col justify-between gap-8">
          <ACartItemCost
            v-if="cartData"
            totalOnly
            :cartData
            :cartItemType
            :item="cartData"
          />
        </div>
      </div>

      <!-- Third Party Integrations -->
      <div
        v-if="showThirdPartyIntegrations"
        class="flex max-w-[500px] flex-col gap-3 pt-4"
      >
        <Divider
          class="before:border-t-2"
          type="dashed"
        />
        <p class="font-semibold">
          {{ $t('cart.label.thirdPartyIntegrations') }}
        </p>
        <p class="text-sm text-slate-500">
          {{ $t('cart.label.thirdPartyIntegrationsSubtitle') }}
        </p>
        <div class="flex flex-col gap-2">
          <div
            v-if="showDocupet"
            class="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 transition-colors hover:bg-gray-50"
            @click="sendDocupet = !sendDocupet"
          >
            <Checkbox
              :modelValue="sendDocupet"
              :binary="true"
              :inputId="'docupet-checkbox'"
              @update:modelValue="sendDocupet = $event"
              @click.stop
            />
            <label
              :for="'docupet-checkbox'"
              class="flex-1 cursor-pointer"
            >
              <span class="font-semibold">{{ $t('cart.label.docupet') }}</span>
              <p class="mt-0.5 text-sm text-slate-500">
                {{ $t('cart.label.docupetDescription') }}
              </p>
            </label>
          </div>
          <div
            v-if="showHills"
            class="flex cursor-pointer items-start gap-3 rounded border border-gray-200 p-3 transition-colors hover:bg-gray-50"
            @click="sendHills = !sendHills"
          >
            <Checkbox
              :modelValue="sendHills"
              :binary="true"
              :inputId="'hills-checkbox'"
              @update:modelValue="sendHills = $event"
              @click.stop
            />
            <label
              :for="'hills-checkbox'"
              class="flex-1 cursor-pointer"
            >
              <span class="font-semibold">{{ $t('cart.label.hillsPetNutrition') }}</span>
              <p class="mt-0.5 text-sm text-slate-500">
                {{ $t('cart.label.hillsPetNutritionDescription') }}
              </p>
            </label>
          </div>
        </div>
      </div>
    </main>

    <footer class="flex-shrink">
      <div class="flex justify-between">
        <Button
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :loading
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />

        <div class="flex flex-wrap gap-2">
          <Button
            v-if="shelterHasOffsiteAdoption"
            class="af-button ml-auto w-fit"
            icon="pi pi-clock"
            severity="info"
            :loading
            :disabled="disableOffsiteButton"
            :label="$t('cart.label.offsite')"
            @click="onNextStep('offsite')"
          />

          <Button
            v-if="showPrePaymentOption"
            class="af-button ml-auto w-fit"
            icon="pi pi-save"
            severity="info"
            :loading
            :label="$t(preAdoptLabel)"
            @click="onNextStep('preAdopt')"
          />

          <Button
            class="af-button af-button-success ml-auto"
            iconPos="right"
            :loading
            :label="$t('cart.label.submitAndComplete')"
            @click="onNextStep(null)"
          >
            <template #icon>
              <FaIcon
                class="mr-2"
                :icon="$icons.faPaw"
              />
            </template>
          </Button>
        </div>
      </div>
    </footer>
  </div>
</template>
